import React from 'react';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

// Flerpie dark theme colors
const COLORS = ['#ffcc33', '#34d399', '#a855f7', '#f97316', '#60a5fa', '#f472b6', '#22d3ee'];
const GRID_COLOR = 'rgba(55,65,81,0.5)';
const AXIS_COLOR = '#6b7280';
const TOOLTIP_BG = '#1f2937';

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-lg"
      style={{ background: TOOLTIP_BG, border: '1px solid rgba(255,204,51,0.1)' }}
    >
      <p className="text-gray-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <span className="font-semibold text-gray-200">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
        </p>
      ))}
    </div>
  );
};

const FlerpieBarChart = ({ data, xKey, yKey }) => {
  const yKeys = Array.isArray(yKey) ? yKey : [yKey];
  return (
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
          <XAxis dataKey={xKey} tick={{ fill: AXIS_COLOR, fontSize: 10 }} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
          <YAxis tick={{ fill: AXIS_COLOR, fontSize: 10 }} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
          <Tooltip content={<ChartTooltip />} />
          {yKeys.map((key, i) => (
            <Bar key={key} dataKey={key} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const FlerpieLineChart = ({ data, xKey, yKey }) => {
  const yKeys = Array.isArray(yKey) ? yKey : [yKey];
  return (
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
          <XAxis dataKey={xKey} tick={{ fill: AXIS_COLOR, fontSize: 10 }} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
          <YAxis tick={{ fill: AXIS_COLOR, fontSize: 10 }} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
          <Tooltip content={<ChartTooltip />} />
          {yKeys.map((key, i) => (
            <Line key={key} type="monotone" dataKey={key} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const FlerpiePieChart = ({ data, nameKey, valueKey }) => (
  <div style={{ width: '100%', height: 200 }}>
    <ResponsiveContainer>
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          outerRadius={70}
          innerRadius={35}
          paddingAngle={2}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={COLORS[i % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<ChartTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  </div>
);

const FlerpieTable = ({ data, columns, title }) => (
  <div className="overflow-x-auto" style={{ maxHeight: 240 }}>
    <table className="w-full text-[11px]">
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col}
              className="text-left px-2 py-1.5 font-semibold text-gray-400 sticky top-0"
              style={{ background: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,204,51,0.08)' }}
            >
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? '' : 'bg-white/[0.02]'}>
            {columns.map((col) => (
              <td key={col} className="px-2 py-1 text-gray-300">
                {row[col] ?? '—'}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Renders a visualization from Flerpie's response.
 * Validates the data structure and fails silently on bad input.
 */
const FlerpieChart = ({ visualization }) => {
  if (!visualization) return null;

  const { type, title, data, xKey, yKey, nameKey, valueKey, columns } = visualization;

  // Validate common requirements
  if (!type || !Array.isArray(data) || data.length === 0) {
    console.warn('[FlerpieChart] Invalid visualization data:', visualization);
    return null;
  }

  let chart = null;

  switch (type) {
    case 'bar':
      if (!xKey || !yKey || !(xKey in data[0])) break;
      chart = <FlerpieBarChart data={data} xKey={xKey} yKey={yKey} title={title} />;
      break;
    case 'line':
      if (!xKey || !yKey || !(xKey in data[0])) break;
      chart = <FlerpieLineChart data={data} xKey={xKey} yKey={yKey} title={title} />;
      break;
    case 'pie':
      if (!nameKey || !valueKey || !(nameKey in data[0])) break;
      chart = <FlerpiePieChart data={data} nameKey={nameKey} valueKey={valueKey} title={title} />;
      break;
    case 'table':
      if (!columns || !Array.isArray(columns) || columns.length === 0) break;
      chart = <FlerpieTable data={data} columns={columns} title={title} />;
      break;
    default:
      console.warn('[FlerpieChart] Unknown chart type:', type);
      return null;
  }

  if (!chart) {
    console.warn('[FlerpieChart] Missing required keys for type:', type, visualization);
    return null;
  }

  return (
    <div
      className="mt-2.5 rounded-lg overflow-hidden"
      style={{
        background: 'rgba(0,0,0,0.2)',
        border: '1px solid rgba(255,204,51,0.06)',
      }}
    >
      {title && (
        <div className="px-3 pt-2.5 pb-1">
          <span className="text-[11px] font-semibold text-gray-400">{title}</span>
        </div>
      )}
      <div className="px-2 pb-2">
        {chart}
      </div>
    </div>
  );
};

export default FlerpieChart;
